/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export interface BarDatum {
  readonly label: string;
  readonly value: number;
}

interface BarChartCardProps {
  readonly title: string;
  readonly data: readonly BarDatum[];
  /** Bar fill — defaults to the current portal accent. */
  readonly color?: string;
  /** Optional sub-label under the title (Figma "Last 6 months"). */
  readonly subtitle?: string;
}

/** A titled bar chart card (Figma analytics). Categorical label → integer value. */
export function BarChartCard({ title, data, color, subtitle }: BarChartCardProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      {subtitle !== undefined && <p className="mb-4 text-xs text-slate-400">{subtitle}</p>}
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data as BarDatum[]} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} />
          <Tooltip cursor={{ fill: '#f1f5f9' }} />
          <Bar dataKey="value" fill={color ?? '#5b47db'} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
